export const DEFAULT_CALENDAR_CARD_APPEARANCE = {
  breakEvenColor: '#3b82f6',
  breakEvenOpacityPercent: 100,
  winColor: '#22c55e',
  winOpacityPercent: 100,
  lossColor: '#ef4444',
  lossOpacityPercent: 100,
};

export const CALENDAR_CARD_APPEARANCE_STORAGE_KEY = 'calendarCardAppearance';

export const MIN_CALENDAR_CARD_OPACITY_PERCENT = 0;
export const MAX_CALENDAR_CARD_OPACITY_PERCENT = 100;
export const MIN_BREAK_EVEN_OPACITY_PERCENT = MIN_CALENDAR_CARD_OPACITY_PERCENT;
export const MAX_BREAK_EVEN_OPACITY_PERCENT = MAX_CALENDAR_CARD_OPACITY_PERCENT;
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

const LIGHT_STYLE_ALPHA = {
  breakEven: {
    month: { background: 0.04, border: 0.08 },
    day: { background: 0.05, border: 0.08 },
  },
  win: {
    month: { background: 0.12, border: 0.25 },
    day: {
      low: { background: 0.08, border: 0.18 },
      medium: { background: 0.10, border: 0.20 },
      high: { background: 0.12, border: 0.25 },
      max: { background: 0.15, border: 0.30 },
    },
  },
  loss: {
    month: { background: 0.12, border: 0.25 },
    day: {
      low: { background: 0.08, border: 0.18 },
      medium: { background: 0.10, border: 0.20 },
      high: { background: 0.12, border: 0.25 },
      max: { background: 0.15, border: 0.30 },
    },
  },
};

const DARK_STYLE_ALPHA = {
  breakEven: {
    month: { background: 0.12, border: 0.30 },
    day: { background: 0.12, border: 0.30 },
  },
  win: {
    month: { background: 0.12, border: 0.30 },
    day: {
      low: { background: 0.08, border: 0.25 },
      medium: { background: 0.10, border: 0.28 },
      high: { background: 0.12, border: 0.30 },
      max: { background: 0.15, border: 0.35 },
    },
  },
  loss: {
    month: { background: 0.12, border: 0.30 },
    day: {
      low: { background: 0.08, border: 0.25 },
      medium: { background: 0.10, border: 0.28 },
      high: { background: 0.12, border: 0.30 },
      max: { background: 0.15, border: 0.35 },
    },
  },
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function normalizeHexColor(value, fallback) {
  if (typeof value !== 'string') return fallback;
  const normalized = value.trim().toLowerCase();
  return HEX_COLOR_PATTERN.test(normalized) ? normalized : fallback;
}

function sanitizeOpacityPercent(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.round(clamp(parsed, MIN_CALENDAR_CARD_OPACITY_PERCENT, MAX_CALENDAR_CARD_OPACITY_PERCENT));
}

function hexToRgb(hex) {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return null;

  const parsed = Number.parseInt(normalized, 16);
  if (!Number.isFinite(parsed)) return null;

  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255,
  };
}

function getTier(absPnl) {
  if (absPnl >= 700) return 'max';
  if (absPnl >= 300) return 'high';
  if (absPnl >= 100) return 'medium';
  return 'low';
}

function toRgbaString({ r, g, b }, alpha) {
  const normalizedAlpha = Math.round(clamp(alpha, 0, 1) * 100) / 100;
  return `rgba(${r}, ${g}, ${b}, ${normalizedAlpha.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')})`;
}

function toRgbString({ r, g, b }) {
  return `rgb(${r}, ${g}, ${b})`;
}

export function sanitizeCalendarCardAppearance(value) {
  const appearance = value && typeof value === 'object' ? value : {};

  return {
    breakEvenColor: normalizeHexColor(
      appearance.breakEvenColor,
      DEFAULT_CALENDAR_CARD_APPEARANCE.breakEvenColor
    ),
    breakEvenOpacityPercent: sanitizeOpacityPercent(
      appearance.breakEvenOpacityPercent,
      DEFAULT_CALENDAR_CARD_APPEARANCE.breakEvenOpacityPercent
    ),
    winColor: normalizeHexColor(
      appearance.winColor,
      DEFAULT_CALENDAR_CARD_APPEARANCE.winColor
    ),
    winOpacityPercent: sanitizeOpacityPercent(
      appearance.winOpacityPercent,
      DEFAULT_CALENDAR_CARD_APPEARANCE.winOpacityPercent
    ),
    lossColor: normalizeHexColor(
      appearance.lossColor,
      DEFAULT_CALENDAR_CARD_APPEARANCE.lossColor
    ),
    lossOpacityPercent: sanitizeOpacityPercent(
      appearance.lossOpacityPercent,
      DEFAULT_CALENDAR_CARD_APPEARANCE.lossOpacityPercent
    ),
  };
}

export function getCalendarCardTradeStyles({
  appearance = DEFAULT_CALENDAR_CARD_APPEARANCE,
  outcome = 'neutral',
  isMonthView = false,
  isDarkMode = false,
  absPnl = 0,
}) {
  if (!['breakEven', 'win', 'loss'].includes(outcome)) {
    return {
      tier: null,
      background: undefined,
      borderColor: undefined,
      pnlTextColor: undefined,
      percentageTextColor: undefined,
    };
  }

  const resolvedAppearance = sanitizeCalendarCardAppearance(appearance);
  const colorHexByOutcome = {
    breakEven: resolvedAppearance.breakEvenColor,
    win: resolvedAppearance.winColor,
    loss: resolvedAppearance.lossColor,
  };

  const baseRgb = hexToRgb(colorHexByOutcome[outcome]) || hexToRgb(DEFAULT_CALENDAR_CARD_APPEARANCE.breakEvenColor);
  // The selected outcome color is the source of truth everywhere. Keep the
  // exact RGB channels for card text, borders, and translucent backgrounds;
  // only the user-controlled card opacity should alter its appearance.
  const accentRgb = baseRgb;
  const textRgb = baseRgb;
  const alphaConfig = isDarkMode ? DARK_STYLE_ALPHA : LIGHT_STYLE_ALPHA;
  const opacityPercentByOutcome = {
    breakEven: resolvedAppearance.breakEvenOpacityPercent,
    win: resolvedAppearance.winOpacityPercent,
    loss: resolvedAppearance.lossOpacityPercent,
  };
  const opacityScale = (opacityPercentByOutcome[outcome] ?? 100) / 100;
  const tier = outcome === 'breakEven' ? 'breakEven' : getTier(absPnl);

  const alphaValues = isMonthView
    ? alphaConfig[outcome].month
    : outcome === 'breakEven'
      ? alphaConfig[outcome].day
      : alphaConfig[outcome].day[tier];

  return {
    tier,
    background: toRgbaString(accentRgb, alphaValues.background * opacityScale),
    borderColor: toRgbaString(accentRgb, alphaValues.border * opacityScale),
    pnlTextColor: toRgbString(textRgb),
    percentageTextColor: toRgbString(textRgb),
  };
}
